import { DATE_FORMATS, parseDateForInput } from '@/utils/formUtils';

// `{ key, label }` as the server expands a stored enum key.
const isEnumObject = (value) =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  typeof value.key === 'string' &&
  'label' in value;

/**
 * Every enum in the group as its key, including ones the form does not
 * render: a tile save sends the whole group back as loaded, and the server
 * stores a string there. `selectedSortingType` has no input and went back as
 * `{ key, label }` — every profile save of the group answered 500.
 */
const enumKeysOf = (group) =>
  Object.fromEntries(
    Object.entries(group || {})
      .filter(([, value]) => isEnumObject(value))
      .map(([name, value]) => [name, value.key])
  );

/**
 * Turns a company from GET /companies/:id into the shape the company form
 * expects (enum objects → keys, dates → dayjs). Shared by the edit page and
 * the company profile.
 */
export default function normalizeCompanyFormData(companyData) {
  const {
    applicationSettings,
    calculationSettings,
    allowances,
    currencies,
    otherAllowances,
  } = companyData || {};

  return {
    ...companyData,
    ownershipStructure: companyData?.ownershipStructure?.map((owner) => ({
      ...owner,
      type: owner.type.key,
      typeOfDocument: owner.typeOfDocument || undefined,
    })),
    applicationSettings: {
      ...applicationSettings,
      ...enumKeysOf(applicationSettings),
      trackerStartDate:
        parseDateForInput(
          applicationSettings?.trackerStartDate,
          DATE_FORMATS.STANDARD
        ) ?? undefined,
      typeOfCompany: applicationSettings?.typeOfCompany?.key,
      typeOfHr: applicationSettings?.typeOfHr?.key,
      documentReturnType: applicationSettings?.documentReturnType?.key,
      documentSeparateRequestResponse:
        applicationSettings?.documentSeparateRequestResponse?.key,
    },
    allowances: Object.fromEntries(
      [
        'mealAllowance',
        'holidayAllowance',
        'travelAllowance',
        'workFromHomeAllowance',
      ].map((key) => [key, allowances?.[key]?.key])
    ),
    otherAllowances: Object.fromEntries(
      ['otherIncome', 'otherExpenses', 'suspension', 'bonus'].map((key) => [
        key,
        otherAllowances?.[key]?.key,
      ])
    ),
    currencies: Object.fromEntries(
      [
        'mealAllowanceCurrency',
        'holidayAllowanceCurrency',
        'travelAllowanceCurrency',
        'workFromHomeAllowanceCurrency',
      ].map((key) => [key, currencies?.[key]?.key])
    ),
    calculationSettings: {
      ...calculationSettings,
      workTypes: calculationSettings?.workTypes
        ?.filter(({ dropdown }) => dropdown)
        ?.map((item) => item.key),
    },
  };
}
